/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect, useMemo } from 'react';
import { EntryPointContainer } from 'react-relay';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { MAIN_CONTENT } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import {
	ReportSSRFailOnUse,
	DisableSSRFailDetection,
} from '@jira/ssr__rendering-guard/src/index.tsx';
import ApdexProvider from '@atlassian/jira-providers-spa-apdex-analytics/src/provider.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import {
	SpaStateContainer,
	SpaStateAppStartWithMatchCheck,
} from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import type {
	Route,
	Location,
	HistoryAction,
	Match,
	Query,
} from '@atlassian/react-resource-router';

const LOCATION = 'route-entry-point-container';
export const EmptySkeleton = (): null => null;
const EMPTY_OBJECT = {};

/**
 * Orchestrates the rendering process for a route's entry point within a single-page application.
 * It intelligently manages page transitions, displaying a skeleton screen while the entry point is loading.
 * The component also handles special cases like redirects by incorporating logic to render or skip the
 * entry point accordingly. This ensures a smooth user experience during navigation and page load events.
 */
export const RouteEntryPointContainer = ({
	location,
	route,
	match,
	action,
	query,
}: {
	location: Location;
	route: Route;
	match: Match;
	action: HistoryAction;
	query: Query;
}): React.JSX.Element | null => {
	const entryPointReference = usePageLayoutEntryPoint(MAIN_CONTENT);

	// in order to show a spinner while page loading -> `route.skeleton=Spinner`
	const Skeleton = useMemo(() => route.skeleton || EmptySkeleton, [route.skeleton]);
	useEffect(() => {
		if (route.isRedirect) {
			log.safeWarnWithoutCustomerData(
				LOCATION,
				`EntryPoint was used inside a redirect component: ${route.name}`,
			);
		}
	}, [route.isRedirect, route.name]);

	const pageId = useMemo(() => {
		let _pageId: string = route.name;
		if (route.pageId) {
			if (typeof route.pageId === 'function') {
				_pageId = route.pageId({
					route,
					action,
					location,
					match,
					query,
				});
			} else {
				_pageId = route.pageId;
			}
		}
		return _pageId;
	}, [action, location, match, query, route]);

	const isPageHydrating = useIsHydrateRenderMode();

	if (!entryPointReference) {
		return null;
	}

	const fallback = (
		<>
			{route.bifrost?.bifrostGroup !== 'shell-only' && (
				<ReportSSRFailOnUse
					pageId={pageId}
					fallbackId="RouteEntryPointContainer"
					ignoreWhenCircuitOpen
					displayErrorInStaging
				/>
			)}
			<Skeleton />
		</>
	);

	const page = (
		<DisableSSRFailDetection>
			<EntryPointContainer
				entryPointReference={entryPointReference}
				// oxlint-disable-next-line typescript/consistent-type-assertions
				props={EMPTY_OBJECT as never}
			/>
		</DisableSSRFailDetection>
	);

	return route.isRedirect ? (
		<EntryPointReferencePlaceholder
			entryPointReference={entryPointReference}
			fallback={<Skeleton />}
			name="route_entry_point_redirect"
		>
			<EntryPointContainer
				entryPointReference={entryPointReference}
				// oxlint-disable-next-line typescript/consistent-type-assertions
				props={EMPTY_OBJECT as never}
			/>
		</EntryPointReferencePlaceholder>
	) : (
		<ApdexProvider pageId={pageId}>
			<SpaStateContainer isGlobal appId={pageId}>
				<SpaStateAppStartWithMatchCheck />
				<EntryPointReferencePlaceholder
					entryPointReference={entryPointReference}
					fallback={fallback}
					name="route_entry_point"
					isSSRPlaceholderEnabled={
						!isPageHydrating ? route?.entryPointOptions?.isSSRPlaceholderEnabled : undefined
					}
				>
					{page}
				</EntryPointReferencePlaceholder>
			</SpaStateContainer>
		</ApdexProvider>
	);
};
